import { useMemo, useState } from 'react'
import BigNumber from 'bignumber.js'
import { Box, Button, Center, Flex, Grid, HStack, Text, VStack } from '@chakra-ui/react'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { EXPLORERS } from '@/constants/constants'
import { getTokenByDenom } from '@/services/token-service'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTRPCClient } from '@/api/trpc/context'
import { useSession } from '@/context/SessionProvider'
import { Spinner } from '@/components/atoms/Spinner'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { useTranslation } from 'next-i18next/pages'

type OperationType = 'bet' | 'win' | 'topup' | 'return'
type FilterType = 'all' | OperationType

type HistoryItem = {
  type: OperationType
  direction: 'credit' | 'debit'
  title: string
  amount: string
  denom: string
  date: string
  status: 'Completed' | 'Pending' | 'Failed' | 'Cancelled'
  txHash?: string
}

const PAGE_SIZE = 20

const filterOptions: Array<{ id: FilterType; labelKey: string }> = [
  { id: 'all', labelKey: 'game-zone.filter-all' },
  { id: 'bet', labelKey: 'game-zone.filter-bets' },
  { id: 'win', labelKey: 'game-zone.filter-wins' },
  { id: 'topup', labelKey: 'game-zone.filter-topups' },
  { id: 'return', labelKey: 'game-zone.filter-returns' },
]

const statusStyles: Record<HistoryItem['status'], { bg: string; dot: string; color: string }> = {
  Completed: { bg: 'rgba(104, 242, 118, 0.12)', dot: '#68F276', color: '#68F276' },
  Pending: { bg: 'rgba(255, 167, 35, 0.12)', dot: '#FFA723', color: '#FFA723' },
  Failed: { bg: 'rgba(255, 125, 125, 0.12)', dot: '#FF7D7D', color: '#FF7D7D' },
  Cancelled: { bg: 'rgba(135, 132, 183, 0.12)', dot: '#8784B7', color: '#8784B7' },
}

const formatTxLabel = (txHash?: string) => {
  if (!txHash) return '—'
  return `${txHash.slice(0, 6)}…${txHash.slice(-4)}`
}

const mapTransactionStatus = (status: unknown): HistoryItem['status'] => {
  const normalizedStatus = String(status ?? '').toLowerCase()

  if (['executed', 'complete', 'confirmed', 'success', 'successful'].includes(normalizedStatus)) return 'Completed'
  if (['failed', 'failure', 'error'].includes(normalizedStatus)) return 'Failed'
  if (['cancelled', 'canceled'].includes(normalizedStatus)) return 'Cancelled'

  return 'Pending'
}

const getOperationType = (eventType: string, direction: unknown): OperationType => {
  const isCredit = String(direction).toLowerCase() === 'credit'

  if (eventType.toLowerCase() === 'writebet') {
    return isCredit ? 'win' : 'bet'
  }

  return isCredit ? 'topup' : 'return'
}

export const GameZoneHistory = () => {
  const { t } = useTranslation('common')
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all')
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const trpc = useTRPCClient()
  const account = useSession((state) => state.account)

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['GAME_ZONE_TRANSACTION_HISTORY', account?.injectiveAddress],
    initialPageParam: 1,
    enabled: !!account,
    queryFn: ({ pageParam }) =>
      trpc.private.games.getUserTransactions.query({
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => (lastPage?.pagination.hasMore ? lastPage.pagination.page + 1 : undefined),
  })

  const historyData = useMemo<HistoryItem[]>(() => {
    return (data?.pages.flatMap((page) => page?.transactions ?? []) ?? []).map((transaction) => {
      const isCredit = String(transaction.direction).toLowerCase() === 'credit'
      const type = getOperationType(transaction.event_type, transaction.direction)
      const txHash = typeof transaction.tx_hash === 'string' ? transaction.tx_hash : undefined

      const titles: Record<OperationType, string> = {
        bet: t('game-zone.transaction-bet'),
        win: t('game-zone.transaction-win'),
        topup: t('game-zone.transaction-topup'),
        return: t('game-zone.transaction-return'),
      }

      return {
        type,
        direction: isCredit ? 'credit' : 'debit',
        title: titles[type],
        amount: new BigNumber(transaction.exchange_amount.toString())
          .plus(transaction.exchange_bonus_amount.toString())
          .toFixed(),
        denom: transaction.denom,
        date: new Intl.DateTimeFormat(undefined, {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(new Date(transaction.created_at)),
        status: mapTransactionStatus(transaction.status),
        txHash,
      }
    })
  }, [data, t])

  const filteredRows = useMemo(() => {
    if (selectedFilter === 'all') return historyData
    return historyData.filter((item) => item.type === selectedFilter)
  }, [historyData, selectedFilter])

  const handleViewMore = async () => {
    setIsLoadingMore(true)

    try {
      let shouldFetchNextPage = true
      let loadedPageCount = data?.pages.length ?? 0

      while (shouldFetchNextPage) {
        const result = await fetchNextPage()
        const pages = result.data?.pages ?? []

        if (result.isError || pages.length <= loadedPageCount) return

        loadedPageCount = pages.length
        const lastPage = pages[pages.length - 1]

        if (!lastPage) return

        const hasMatchingTransaction =
          selectedFilter === 'all' ||
          lastPage.transactions.some(
            (transaction) => getOperationType(transaction.event_type, transaction.direction) === selectedFilter
          )

        shouldFetchNextPage = !hasMatchingTransaction && lastPage.pagination.hasMore
      }
    } finally {
      setIsLoadingMore(false)
    }
  }

  if (isLoading) {
    return (
      <Center minH="360px">
        <Spinner />
      </Center>
    )
  }

  if (error) {
    return <NoResultsFallback text={t('game-zone.failed-history')} />
  }

  return (
    <Box w="full" maxW="1280px" mx="auto" py={{ base: 2, md: 4 }} fontFamily="body">
      <VStack align="stretch" spacing={6}>
        <VStack align="flex-start" spacing={1}>
          <Text
            fontSize={{ base: '30px', md: '40px' }}
            fontWeight={700}
            color="white"
            lineHeight="1"
            letterSpacing="-0.02em"
          >
            {t('game-zone.history-title')}
          </Text>
          <Text fontSize="14px" fontWeight={500} color="ninja.200" lineHeight="1.2">
            {t('game-zone.history-description')}
          </Text>
        </VStack>

        <Flex align="center" gap={2} wrap="wrap">
          <HStack spacing={2} wrap="wrap">
            {filterOptions.map((option) => {
              const active = option.id === selectedFilter
              return (
                <Button
                  key={option.id}
                  onClick={() => setSelectedFilter(option.id)}
                  size="sm"
                  px={4}
                  py={2.5}
                  minW={{ base: 'auto', sm: '90px' }}
                  h="38px"
                  borderRadius="12px"
                  border="none"
                  bg={active ? 'linear-gradient(135deg, #8E83FB 31.11%, #5D52EE 97.01%)' : 'ninja.500'}
                  color={active ? 'white' : 'ninja.100'}
                  fontSize="14px"
                  fontWeight={700}
                  _hover={{
                    bg: active ? 'linear-gradient(135deg, #8E83FB 31.11%, #5D52EE 97.01%)' : 'ninja.400',
                  }}
                  _focus={{ boxShadow: 'none' }}
                >
                  {t(option.labelKey)}
                </Button>
              )
            })}
          </HStack>
        </Flex>

        {!account || filteredRows.length === 0 ? (
          <NoResultsFallback text={t(account ? 'game-zone.no-transactions' : 'game-zone.sign-in-history')} />
        ) : (
          <Box borderRadius="16px" overflowX="auto" overflowY="hidden" bg="ninja.600">
            <Grid
              templateColumns="minmax(0, 1.8fr) 200px 180px 160px 120px"
              minW="900px"
              bg="ninja.700"
              px={4}
              py={3}
              color="ninja.200"
              fontSize="12px"
              fontWeight={700}
              letterSpacing="0"
              borderBottom="1px solid rgba(255,255,255,0.04)"
            >
              <Text>{t('game-zone.operation')}</Text>
              <Text>{t('game-zone.amount')}</Text>
              <Text>{t('game-zone.date')}</Text>
              <Text>{t('game-zone.status')}</Text>
              <Text>{t('game-zone.transaction')}</Text>
            </Grid>

            {filteredRows.map((item, index) => {
              const status = statusStyles[item.status]
              const isLast = index === filteredRows.length - 1
              const isCredit = item.direction === 'credit'
              const token = getTokenByDenom(item.denom)
              const tokenMoney = new TokenMoney(item.amount, item.denom)
              const txUrl = item.txHash ? `${EXPLORERS.INJECTIVE_TRANSACTIONS}${item.txHash}` : undefined

              return (
                <Grid
                  key={`${item.title}-${item.date}-${index}`}
                  templateColumns="minmax(0, 1.8fr) 200px 180px 160px 120px"
                  minW="900px"
                  alignItems="center"
                  px={4}
                  py={3.5}
                  borderBottom={isLast ? 'none' : '1px solid rgba(255,255,255,0.04)'}
                  bg={isCredit ? 'rgba(104, 242, 118, 0.025)' : 'rgba(255, 125, 125, 0.025)'}
                  _hover={{ bg: isCredit ? 'rgba(104, 242, 118, 0.06)' : 'rgba(255, 125, 125, 0.06)' }}
                >
                  <Flex align="center" gap={3} minW={0}>
                    <Box
                      w="24px"
                      h="24px"
                      minW="24px"
                      borderRadius="12px"
                      bg={
                        item.type === 'win' || item.type === 'topup'
                          ? 'rgba(104, 242, 118, 0.12)'
                          : 'rgba(154,146,253,0.12)'
                      }
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color={item.type === 'win' || item.type === 'topup' ? '#68F276' : '#9A92FD'}
                      fontWeight={700}
                      fontSize="12px"
                    >
                      {item.type === 'bet' ? '−' : item.type === 'return' ? '↩' : '+'}
                    </Box>
                    <Text
                      color="white"
                      fontSize="14px"
                      fontWeight={700}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {item.title}
                    </Text>
                  </Flex>

                  <Flex align="center" gap={2} minW={0} maxW="100%" flexWrap="nowrap" display="inline-flex">
                    <Text
                      color={isCredit ? '#68F276' : '#FF7D7D'}
                      fontSize="14px"
                      fontWeight={800}
                      whiteSpace="nowrap"
                      lineHeight="1.2"
                    >
                      {isCredit ? '+' : '−'}
                      {tokenMoney.humanFormat}
                    </Text>
                    <Flex align="center" gap={1.5} minW={0} maxW="100%" overflow="hidden">
                      <Box
                        w="20px"
                        h="20px"
                        minW="20px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        sx={{
                          '> *': {
                            display: 'block',
                            width: '20px !important',
                            height: '20px !important',
                          },
                        }}
                      >
                        {token?.icon(20)}
                      </Box>
                      <Text
                        color="white"
                        fontSize="14px"
                        fontWeight={700}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        maxW="72px"
                      >
                        {token?.name || item.denom.toUpperCase()}
                      </Text>
                    </Flex>
                  </Flex>

                  <Text color="ninja.200" fontSize="14px" fontWeight={500} whiteSpace="nowrap">
                    {item.date}
                  </Text>

                  <Box>
                    <Flex align="center" gap={2} bg={status.bg} borderRadius="8px" px={2} py={1} w="fit-content">
                      <Box w="6px" h="6px" borderRadius="50%" bg={status.dot} />
                      <Text color={status.color} fontSize="12px" fontWeight={700} whiteSpace="nowrap">
                        {t(`game-zone.status-${item.status.toLowerCase()}`)}
                      </Text>
                    </Flex>
                  </Box>

                  {txUrl ? (
                    <Text
                      as="a"
                      href={txUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      color="#9A92FD"
                      fontSize="14px"
                      fontWeight={700}
                      whiteSpace="nowrap"
                      _hover={{ opacity: 0.8 }}
                    >
                      {formatTxLabel(item.txHash)}
                    </Text>
                  ) : (
                    <Text color="ninja.300" fontSize="14px" fontWeight={700} whiteSpace="nowrap">
                      —
                    </Text>
                  )}
                </Grid>
              )
            })}
          </Box>
        )}

        {hasNextPage && (
          <Flex justify="center" mt={2}>
            <Button isLoading={isLoadingMore || isFetchingNextPage} onClick={handleViewMore}>
              {t('game-zone.view-more')}
            </Button>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}
